import { Box, TextField, Typography, Autocomplete, CircularProgress, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import axiosService from 'utils/axios';
import { Carrier, Company } from 'types/entities/types';
import ModalPopup from '../../../components/Modal/ModalPopup';
import AddOrEditCompany from '../companies/AddOrEditCompany';

interface Props {
  carrier?: Carrier;
  onSave: (carrier: Carrier) => void;
  modalTitle: string;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

interface CarrierCompany {
  id: string;
  name: string;
}

interface CarrierCreateUpdateError {
  error: string;
}

export default function AddOrEditCarrier(props: Props) {
  const { carrier, onSave, modalTitle, isModalOpen, setModalOpen } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CarrierCreateUpdateError | null>();

  const [companyTextInput, setCompanyTextInput] = useState<string>();
  const [companyOptions, setCompanyOptions] = useState<CarrierCompany[]>([]);
  const [isCompanyOptionsLoading, setIsCompanyOptionsLoading] = useState(false);
  const [addNewCompanyModalOpen, setAddNewCompanyModalOpen] = useState(false);

  const [companyChoice, setCompanyChoice] = useState<CarrierCompany | null>(
    carrier?.company
      ? {
          id: carrier.company.id,
          name: carrier.company.name,
        }
      : null
  );
  const [notesInput, setNotesInput] = useState(carrier?.notes || '');

  const getCompaniesList = useCallback((onSuccess?: () => void) => {
    setIsCompanyOptionsLoading(true);
    axiosService
      .get(`/entities/company/`)
      .then(res => {
        const names: CarrierCompany[] = [];
        res.data.forEach((company: Company) => names.push({ id: company.id, name: company.name }));
        setCompanyOptions(names);

        if (onSuccess) {
          onSuccess();
        }
      })
      .finally(() => setIsCompanyOptionsLoading(false));
  }, []);

  useEffect(() => {
    // Fetch companies list
    getCompaniesList();
  }, [getCompaniesList]);

  const handleSubmit = () => {
    setLoading(true);
    const data = {
      // eslint-disable-next-line camelcase
      company_id: companyChoice?.id || '',
      notes: notesInput,
    };
    if (carrier) {
      // Update existing carrier
      axiosService
        .patch(`/entities/carrier/${carrier.id}/`, data)
        .then(carrierData => {
          setLoading(false);
          setError(null);
          onSave(carrierData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    } else {
      // Create new carrier
      axiosService
        .post('/entities/carrier/', data)
        .then(carrierData => {
          setLoading(false);
          setError(null);
          onSave(carrierData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    }
  };

  return (
    <ModalPopup
      title={modalTitle}
      open={isModalOpen}
      onClose={() => setModalOpen(false)}
      actionButtons={
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          type="submit"
          fullWidth
          variant="contained"
          disabled={!companyChoice}
        >
          {carrier ? 'Edit carrier' : 'Create carrier'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', mt: 1 }}>
          {error ? <Typography>{error.error}</Typography> : null}
          <Autocomplete
            fullWidth
            sx={{ mt: 1, mb: 1 }}
            value={companyChoice}
            onChange={(event, value) => setCompanyChoice(value)}
            onInputChange={(event, value) => setCompanyTextInput(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={option => option.name}
            options={companyOptions}
            loading={isCompanyOptionsLoading}
            renderInput={params => (
              <TextField
                {...params}
                required
                label="Company"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              <Button fullWidth onClick={() => setAddNewCompanyModalOpen(true)}>
                Add a new Company
              </Button>
            }
          />
          {addNewCompanyModalOpen ? (
            <AddOrEditCompany
              companyName={companyTextInput}
              onSave={newCompany => {
                getCompaniesList(() => {
                  setCompanyChoice({
                    id: newCompany.id,
                    name: newCompany.name,
                  });
                  setAddNewCompanyModalOpen(false);
                });
              }}
              modalTitle="Add new company"
              isModalOpen={addNewCompanyModalOpen}
              setModalOpen={setAddNewCompanyModalOpen}
            />
          ) : null}
          <TextField
            fullWidth
            minRows={5}
            multiline
            margin="normal"
            id="notes"
            label="Notes"
            value={notesInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNotesInput(event.target.value);
            }}
          />
        </Box>
      </Box>
    </ModalPopup>
  );
}
